/**
 * One app on What asks first (slice 3 §8, Paper board F): Crew's default
 * holds first, always shown to you, then the app's other writes grouped by
 * Composio's tags (Deletes, Changes, Adds something), each with Show me first
 * or Just do it, then one line for what only looks. Every name and line is
 * Composio's; Crew's only words are the groups' and this page's own.
 */
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";
import { changedLine, groupsOf, moreNote, type ActionRow, type AskApp } from "./ask-first-view";
import { AppIcon } from "./integrations-parts";

/** A group's rows shown before "Show N more". */
const FIRST = 3;
/** Crew's own group shows this many as rows, then names the rest. */
const ALWAYS_ROWS = 2;

type OnSet = (action: string, ask_first: boolean | null) => void;

export function AppPanel({
  app,
  rows,
  timeZone,
  busy,
  error,
  onSet,
}: {
  app: AskApp;
  rows: ActionRow[];
  timeZone: string;
  busy: boolean;
  error: string | null;
  onSet: OnSet;
}) {
  const { always, groups } = groupsOf(rows);
  return (
    <section
      aria-label={app.name}
      data-ask-first={app.app}
      className="min-w-0 flex-1 overflow-hidden rounded-panel bg-card shadow-[0_0_0_1px_var(--color-border)]"
    >
      <div className="flex items-center gap-3.5 border-b border-border px-4 py-4 md:px-7 md:py-5">
        <span className="hidden md:block">
          <AppIcon app={app.app} icon={app.icon ?? undefined} mark={app.mark} />
        </span>
        <div className="flex flex-col gap-0.5">
          <h3 className="text-[18px] leading-6 font-semibold text-foreground">{app.name}</h3>
          <p className="text-meta leading-[19px] text-muted-foreground">
            Applies to every {app.name} account in your crew.
            {app.connected ? null : " Nobody has connected it yet: these apply once someone does."}
          </p>
        </div>
      </div>
      {error ? (
        <p role="alert" className="px-4 pt-3 text-meta text-destructive md:px-7">
          {error}
        </p>
      ) : null}
      {always.length ? <AlwaysGroup rows={always} /> : null}
      {groups.map((g) => (
        <WriteGroup
          key={g.group}
          title={g.title}
          rows={g.rows}
          timeZone={timeZone}
          busy={busy}
          onSet={onSet}
        />
      ))}
      <OnlyLooks reads={app.reads} />
    </section>
  );
}

/** Crew's default holds: they reach other people, so they always show you first. */
function AlwaysGroup({ rows }: { rows: ActionRow[] }) {
  const shown = rows.slice(0, ALWAYS_ROWS);
  const rest = rows.slice(ALWAYS_ROWS);
  return (
    <div
      data-ask-first-group="always"
      className="flex flex-col border-b border-border bg-copper/[0.04] px-4 pt-4 pb-2 md:px-7 md:pt-5"
    >
      <GroupHead title="Always shown to you first" tone="copper">
        These reach other people, so Crew always asks. They can’t be switched to “Just do it”.
      </GroupHead>
      {shown.map((r) => (
        <Row key={r.action} row={r} timeZone="UTC" busy onSet={() => {}} />
      ))}
      {rest.length ? (
        <p className="border-t border-copper/15 py-3 text-meta text-muted-foreground">
          Also: {rest.map((r) => r.name).join(", ")}
        </p>
      ) : null}
    </div>
  );
}

/** The group's rows in an order fixed when it appears, the ones showing you first first, so a row never moves under a tap. */
function useOrder(rows: ActionRow[]) {
  const [order] = useState(() =>
    [...rows].sort((a, b) => Number(b.ask_first) - Number(a.ask_first)).map((r) => r.action),
  );
  return order
    .map((id) => rows.find((r) => r.action === id))
    .filter((r): r is ActionRow => r !== undefined);
}

/**
 * Whether the group is open. On a phone, one with nothing to show you first
 * starts closed (board F, 390). Read each render, as the phone check isn't
 * known on the first one; once a row shows you first it stays open, so
 * putting that row back doesn't shut the group under the owner's tap.
 */
function useOpen(rows: ActionRow[]): [boolean, () => void] {
  const mobile = useIsMobile();
  const asking = rows.some((r) => r.ask_first);
  const [opened, setOpen] = useState(false);
  // React's state-from-props pattern: set in render, once.
  if (asking && !opened) setOpen(true);
  return [opened || asking || !mobile, () => setOpen(true)];
}

function WriteGroup({
  title,
  rows,
  timeZone,
  busy,
  onSet,
}: {
  title: string;
  rows: ActionRow[];
  timeZone: string;
  busy: boolean;
  onSet: OnSet;
}) {
  const sorted = useOrder(rows);
  const [open, setOpen] = useOpen(rows);
  const [all, setAll] = useState(false);
  const shown = all ? sorted : sorted.slice(0, FIRST);
  return (
    <div
      data-ask-first-group={title}
      className="flex flex-col border-b border-border px-4 pt-4 pb-2 md:px-7 md:pt-5"
    >
      {open ? (
        <>
          <GroupHead title={title} count={rows.length} />
          {shown.map((r) => (
            <Row key={r.action} row={r} timeZone={timeZone} busy={busy} onSet={onSet} />
          ))}
          {all ? null : <ShowMore hidden={sorted.slice(FIRST)} onShow={() => setAll(true)} />}
        </>
      ) : (
        <ClosedHead title={title} rows={rows} onOpen={setOpen} />
      )}
    </div>
  );
}

/** A closed group on a phone: its title, what its rows do, and a chevron to open it. */
function ClosedHead({
  title,
  rows,
  onOpen,
}: {
  title: string;
  rows: ActionRow[];
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={false}
      onClick={onOpen}
      className="flex items-center gap-2 pb-2 text-left"
    >
      <GroupTitle title={title} count={rows.length} />
      <span className="flex-1 text-right text-meta text-muted-foreground">{moreNote(rows)}</span>
      <ChevronRight aria-hidden className="size-4 text-hint" />
    </button>
  );
}

/** "Show 8 more · All just do it", when rows are hidden. */
function ShowMore({ hidden, onShow }: { hidden: ActionRow[]; onShow: () => void }) {
  if (hidden.length === 0) return null;
  return (
    <p className="flex flex-wrap items-baseline gap-x-2 border-t border-border py-3 text-meta">
      <button
        type="button"
        onClick={onShow}
        className="font-medium text-foreground underline underline-offset-2"
      >
        Show {hidden.length} more
      </button>
      <span className="text-muted-foreground">{moreNote(hidden)}</span>
    </p>
  );
}

function GroupTitle({ title, count, tone }: { title: string; count?: number; tone?: "copper" }) {
  return (
    <span className="flex items-baseline gap-2.5">
      <span
        className={cn(
          "text-xs font-semibold tracking-label uppercase",
          tone ? "text-copper" : "text-muted-foreground",
        )}
      >
        {title}
      </span>
      {count === undefined ? null : <span className="text-xs text-hint">{count}</span>}
    </span>
  );
}

function GroupHead({
  title,
  count,
  tone,
  children,
}: {
  title: string;
  count?: number;
  tone?: "copper";
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 pb-2">
      <GroupTitle title={title} count={count} tone={tone} />
      {children ? <p className="text-meta text-muted-foreground">{children}</p> : null}
    </div>
  );
}

function Row({
  row,
  timeZone,
  busy,
  onSet,
}: {
  row: ActionRow;
  timeZone: string;
  busy: boolean;
  onSet: OnSet;
}) {
  const changed = changedLine(row, timeZone);
  return (
    <div
      data-ask-first-action={row.action}
      className={cn(
        "flex flex-col gap-2 border-t py-3 md:flex-row md:items-center md:gap-4",
        row.locked ? "border-copper/15" : "border-border",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-body text-foreground">{row.name}</span>
        {row.line ? <span className="text-meta text-muted-foreground">{row.line}</span> : null}
        {changed ? (
          <span className="text-meta text-copper">
            {changed} ·{" "}
            <button
              type="button"
              disabled={busy}
              onClick={() => onSet(row.action, null)}
              className="underline underline-offset-2"
            >
              Put it back
            </button>
          </span>
        ) : null}
      </div>
      <Choice row={row} busy={busy} onSet={onSet} />
    </div>
  );
}

/** Show me first, or Just do it: Crew's default holds can't be moved off the first. */
function Choice({ row, busy, onSet }: { row: ActionRow; busy: boolean; onSet: OnSet }) {
  const options = [
    { value: true, label: "Show me first" },
    { value: false, label: "Just do it" },
  ];
  return (
    <div
      role="radiogroup"
      aria-label={`${row.name}: show me first, or just do it`}
      className="flex w-fit shrink-0 rounded-[9px] bg-foreground/[0.05] p-[3px]"
    >
      {options.map((o) => {
        const on = row.ask_first === o.value;
        return (
          <button
            key={o.label}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={busy || row.locked}
            onClick={() => (on ? undefined : onSet(row.action, o.value))}
            className={cn(
              "rounded-[7px] px-3 py-1.5 text-meta",
              on && "bg-card font-medium shadow-[0_1px_2px_rgb(31_42_68/0.1)]",
              on && (row.locked ? "text-copper" : "text-foreground"),
              !on && (row.locked ? "text-hint" : "text-muted-foreground hover:text-foreground"),
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** What only reads: one line, never asked about. */
function OnlyLooks({ reads }: { reads: number }) {
  if (!reads) return null;
  return (
    <div
      data-ask-first-group="looks"
      className="flex flex-col gap-1 bg-foreground/[0.02] px-4 py-4 md:flex-row md:items-center md:gap-2.5 md:px-7"
    >
      <GroupTitle title="Only looks" count={reads} />
      <span className="text-meta text-muted-foreground">
        Searching and reading. These never need your OK.
      </span>
    </div>
  );
}
